/**
 * MODULE: domain/pages/frontend/hooks/useBrainActivityCrmQuerySync.ts
 * PURPOSE: Invalidate CRM/page brain React Query caches on engagement activity bus [2.2.1251].
 * DEPENDENCIES: brainActivityEvents, crmBrainQueryUtils, crmEntityBrainRefreshUtils, pageBrainFocusUtils
 * PUBLIC_API: useBrainActivityCrmQuerySync
 * SIDE_EFFECTS: React Query invalidation on CustomEvent
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 64 / 100 lines
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  BRAZN_BRAIN_ACTIVITY_CHANGED,
  type BrainActivityChangedDetail,
} from '@shared/lib/frontend/utils/brainActivityEvents';
import { CRM_BRAIN_QUERY_KEYS } from '../utils/crmBrainQueryUtils';
import { invalidateCrmEntityBrainQueries } from '../utils/crmEntityBrainRefreshUtils';
import { brainFocusKindFromContextEntity } from '../utils/pageBrainFocusUtils';

export function useBrainActivityCrmQuerySync(enabled = true): void {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const onActivityChanged = (event: Event) => {
      const detail = (event as CustomEvent<BrainActivityChangedDetail>).detail;
      if (detail?.scope === 'dashboard') return;

      if (detail?.source === 'quota_sync') {
        void qc.invalidateQueries({ queryKey: ['sales-targets'] });
        void qc.invalidateQueries({ queryKey: ['crm-connectors'] });
      }

      const entityId = detail?.entity_id?.trim();
      const kind = brainFocusKindFromContextEntity(detail?.entity_type);
      if (kind && entityId) {
        invalidateCrmEntityBrainQueries(qc, { kind, entityId });
        return;
      }

      void qc.invalidateQueries({ queryKey: CRM_BRAIN_QUERY_KEYS.prefix() });
      void qc.invalidateQueries({ queryKey: ['pages'] });
    };

    window.addEventListener(BRAZN_BRAIN_ACTIVITY_CHANGED, onActivityChanged);
    return () => window.removeEventListener(BRAZN_BRAIN_ACTIVITY_CHANGED, onActivityChanged);
  }, [enabled, qc]);
}
